import { Buffer } from "buffer";

import moment from "moment-timezone";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

import "react-toastify/dist/ReactToastify.css";

import blogimg from "assets/images/blog-img.webp";
import favicon from "assets/images/indianic-short-logo.svg";
import logo from "assets/images/logo.svg";
import profilepic from "assets/images/profile-pic.jpg";
import generalConfig from "configs/generalConfig";
import { DEFAULT_DATE_FORMAT, DEFAULT_LANGUAGE_CODE } from "utils/constants";
import { localStorageKeys } from "utils/localStorageKeys";

/******************* 
@Purpose : Used for show message notification
@Parameter : text, type, autoClose, position
@Author : INIC
******************/
export const messageNotification = (
  text,
  type = "success",
  autoClose = generalConfig.TOAST_TIMER,
  position = "top-right"
) => {
  toast[type](text, {
    position: position,
    autoClose: autoClose,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
  });
};

/******************* 
@purpose : Used for encode data into base 64
@Parameter : {data} 
@Author : IndiaNIC
******************/
export const encodeBase64 = (data) => {
  return Buffer.from(data).toString("base64");
};

/******************* 
@purpose : Used for decode data from base 64
@Parameter : {data} 
@Author : IndiaNIC
******************/
export const decodeBase64 = (data) => {
  return Buffer.from(data, "base64").toString("ascii");
};

/******************* 
@purpose : Used for content type
@Parameter : {} 
@Author : IndiaNIC
******************/
export const getHeader = (formData = false) => {
  return {
    "Content-Type": formData ? "multipart/form-data" : "application/json",
    Accept: "application/json",
  };
};

/******************* 
@purpose : Used for File upload extension
@Parameter : {} 
@Author : IndiaNIC
******************/
export const FILE_UPLOAD_EXTENSION = [
  "image/png", // for .png image
  "image/jpeg", // for .jpeg image
  "image/jpg", // for .jpg image
  // ".svg", // for .svg image
];

/******************* 
@purpose : Used for Remember Me Login
@Parameter : {} 
@Author : IndiaNIC
******************/

export const rememberMe = (clearTheme = true) => {
  const rememberMe = localStorage?.getItem(localStorageKeys.REMEMBER_ME);
  if (!rememberMe) {
    localStorage.clear();
  } else {
    if (clearTheme) {
      localStorage.removeItem(localStorageKeys.THEME);
    }
    localStorage.removeItem(localStorageKeys.EXPIRATIONTIME);
    localStorage.removeItem(localStorageKeys.IS_EMAIL);
    localStorage.removeItem(localStorageKeys.IS_MOBILE);
    localStorage.removeItem(localStorageKeys.ma_la);
    localStorage.removeItem(localStorageKeys.MASKED_EMAIL);
    localStorage.removeItem(localStorageKeys.MASKED_PHONE);
    localStorage.removeItem(localStorageKeys.OTP_TOKEN);
    localStorage.removeItem(localStorageKeys?.VERIFY_LOGIN_TOKEN);
  }
};

/******************* 
@purpose : Used for show default profile image onerror
@Parameter : {} 
@Author : IndiaNIC
******************/

export const setDefaultProfilePic = (e) => {
  e.currentTarget.src = profilepic;
  e.currentTarget.onerror = null;
};

/******************* 
@purpose : Used for show default profile image onerror
@Parameter : {} 
@Author : IndiaNIC
******************/

export const setDefaultLogo = (e) => {
  e.currentTarget.src = logo;
  e.currentTarget.onerror = null;
};
export const setDefaultFavicon = (e) => {
  e.currentTarget.src = favicon;
  e.currentTarget.onerror = null;
};

export const arrayToJson = (array, key) => {
  let data = {};
  for (let obj of array) {
    data[obj[key]] = obj;
  }
  return data;
};

export const jsonToArray = (obj) => Object.values(obj);

/******************* 
@purpose : Used for formatting date 
@Parameter : {date, dateFormat} 
@Author : INIC
******************/
export const getFormattedDate = (date, dateFormat) => {
  if (date === "") {
    return false;
  }
  if (dateFormat) {
    return moment(date).format(dateFormat);
  } else {
    return moment(date).format(DEFAULT_DATE_FORMAT);
  }
};

/******************* 
@purpose : Used for formatting date in date picker date format prop
@Parameter : {date, dateFormat} 
@Author : IndiaNIC
******************/
export const getFormattedDateWithTime = (dateFormat, timeFormat) => {
  if (timeFormat === "12 hours") {
    return `${dateFormat} h:mm aa`;
  } else if (timeFormat === "24 hours") {
    return `${dateFormat} HH:mm`;
  } else {
    return `${dateFormat} h:mm aa`;
  }
};

/******************* 
@purpose : Used for converting date according to timezone
@Parameter : {date, timezone} 
@Author : IndiaNIC
******************/
export const getTimeZoneConvertedDate = (date, timezone) => {
  if (date === "") {
    return false;
  }
  return moment.tz(date, timezone);
};

/******************* 
@purpose : Used for converting date to utc
@Parameter : {date} 
@Author : IndiaNIC
******************/
export const getUTCFormat = (date) => {
  return moment(date).utc().format("YYYY-MM-DDTHH:mm:ss");
};

export const getFormattedTimeWithTimezone = (time, timezone, timeFormat) => {
  if (timeFormat === "12 hours") {
    return moment(time).tz(timezone).format("hh:mm A");
  } else if (timeFormat === "24 hours") {
    return moment(time).tz(timezone).format("HH:mm");
  } else {
    return moment(time).tz(timezone).format("hh:mm A");
  }
};

// export const getFormattedDateTimeWithTimezone = (
//   date,
//   timezone,
//   dateFormat,
//   timeFormat
// ) => {
//   if (timeFormat === "12 hours") {
//     return moment(date)?.tz(timezone)?.format(`${dateFormat}-hh:mm A`);
//   } else if (timeFormat === "24 hours") {
//     return moment(date)?.tz(timezone)?.format(`${dateFormat}-HH:mm`);
//   } else {
//     return moment(date)?.tz(timezone)?.format(`${dateFormat}-hh:mm A`);
//   }
// };
export const getFormattedDateTimeWithTimezone = (
  date,
  timezone,
  dateFormat,
  timeFormat,
  showTime = true
) => {
  if (!date) return "";

  const validTimezone = timezone?.split(") ")[1] || "UTC";

  const isDateOnly = typeof date === "string" && date.length === 10;

  const momentObj = isDateOnly
    ? moment.tz(date, "YYYY-MM-DD", validTimezone) // parse as local date
    : moment(date).tz(validTimezone);

  const formattedDate = momentObj.format(dateFormat);

  if (!showTime) return formattedDate;

  const formattedTime =
    timeFormat === "24 hours"
      ? momentObj.format("HH:mm")
      : momentObj.format("hh:mm A");

  return `${formattedDate} | ${formattedTime}`;
};
/******************* 
@purpose : Used for show alert popup 
@Parameter : {} 
@Author : INIC
******************/
export const fireDeletePopup = async (
  confirmDeleteHandler,
  resetGridRowSelection,
  title
) => {
  const willDelete = await Swal.fire({
    title: title || "Are you sure, you want to delete?",
    icon: "warning",
    buttons: true,
    dangerMode: true,
    showCancelButton: true,
    confirmButtonText: "Delete",
    // cancelButtonText: "Cancel",
    // confirmButtonColor: "#A01B1B",
    // cancelButtonColor: "#999999",
    reverseButtons: true,
    focusConfirm: false,
    focusCancel: true,
  });

  if (willDelete.isConfirmed) {
    confirmDeleteHandler();
    resetGridRowSelection && resetGridRowSelection();
  }
};

export const getLanguageOptions = (data) => {
  return data?.map((list) => ({
    label: `${list?.language}`,
    value: list?._id,
  }));
};

export const getLanguageCode = (data, id) => {
  return id && data
    ? data?.find((list) => list?._id === id)?.languageCode
    : DEFAULT_LANGUAGE_CODE;
};

/******************* 
@purpose : Used for setting default pic in blog card view 
@Parameter : {} 
@Author : INIC
******************/
export const setDefaultBlogImage = (e) => {
  e.currentTarget.src = blogimg;
  e.currentTarget.onerror = null;
};
/******************* 
@purpose : Used for select Option in react select 
@Parameter : {} 
@Author : INIC
******************/
export const selectOptionHandler = (formik, name) => {
  return formik?.values?.[name]
    ? {
        label: formik?.values?.[name],
        value: formik?.values?.[name],
      }
    : undefined;
};

/******************* 
@purpose : Used for conditional ternary 
@Parameter : {} 
@Author : INIC
******************/
export const conditionalTernaryHandler = (condition, param1, param2) => {
  return condition ? param1 : param2;
};

/******************* 
@purpose : Used for common ternary 
@Parameter : {} 
@Author : INIC
******************/
export const commonTernaryHandler = (param, param2) => {
  return param ? param : param2;
};

/******************* 
@purpose : Used for download file
@Parameter : {type,file} 
@Author : INIC
******************/

export const downlaodFiletoType = async (type, filepath) => {
  if (!filepath) {
    messageNotification("File path not found!", "error");
    return;
  }
  const baseUrl = `${process.env.REACT_APP_CSV_EXPORT_URL}`;
  const cleanFilePath = filepath?.replace(/^\/+/, "");

  const file = `${baseUrl}/${cleanFilePath}`;

  try {
    const res = await fetch(file);
    if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);

    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", cleanFilePath.split("/").pop()); // extract filename
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (err) {
    messageNotification("File download failed!", "error");
  }
};

/******************* 
@purpose : Used for sorting array of objects
@Parameter : {arr, key} 
@Author : INIC
******************/
export const sortArrOfObj = (arr, key) => {
  return arr.sort((a, b) => {
    if (a[key] < b[key]) {
      return -1;
    }
    if (a[key] > b[key]) {
      return 1;
    }
    return 0;
  });
};

export const formatUrlPathScopes = (pathname, params, permission) => {
  // Remove any params from URL
  let pathWithoutParams = pathname;
  Object.values(params).forEach((param) => {
    pathWithoutParams = pathWithoutParams?.replace(
      new RegExp(`/${param}(?=/|$)`, "g"),
      ""
    );
  });

  // Get CRUD scope from URL (i.e., view/create/edit)
  const pathScope = Object.values(permission).find((scope) =>
    pathWithoutParams.toLowerCase().includes(scope.toLowerCase())
  );

  // Get URL module path
  const pathWithoutScope = pathScope
    ? pathWithoutParams.replace(
        new RegExp(`/${pathScope.toLowerCase()}(?=/|$)`, "g"),
        ""
      )
    : pathWithoutParams;

  return { pathWithoutParams, pathScope, pathWithoutScope };
};
export const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};
/******************* 
@purpose : Used to renderred the role and permission 
@Parameter : 
@Author : INIC
******************/

export const rolePermissionData = (
  RolePermission,
  moduleCategory,
  role_permission
) => {
  return RolePermission?.find(
    (el) => el.category === moduleCategory
  )?.permissions.find((permission) => permission.permission === role_permission)
    ?.isSelected;
};
